// src/components/Apm/Inv/Inv.js

import { useEffect, useState } from 'react';
import { forwardRef, useImperativeHandle } from 'react';
import dayjs from 'dayjs';
import { FastField, Field, FieldArray, Form, Formik, setNestedObjectValues, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';

import { alpha, styled } from '@mui/material/styles';
import {
  Badge,
  Box,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Popover,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AutoAwesomeMotionIcon from '@mui/icons-material/AutoAwesomeMotion';
import ModeEditOutlineIcon from '@mui/icons-material/ModeEditOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import SaveIcon from '@mui/icons-material/Save';
import SaveAsIcon from '@mui/icons-material/SaveAs';
import RemoveIcon from '@mui/icons-material/Remove';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { setItemForEdit } from '../../../redux/slices/itemForEditSlice';
import { setPopup, setPopupInv, setPopupInv2 } from '../../../redux/slices/popupSlice';
import { setFormTouchedInv } from '../../../redux/slices/formTouchedSlice';
import { useCreateInvMutation, useDeleteInvMutation, useUpdateInvMutation } from '../../../redux/features/invApi';
import { useRetrieveUserQuery } from '../../../redux/features/authApi';
import { useGetImageQuery } from '../../../redux/features/imageApi';
import AutocompleteWrapper from '../../Controls/FormsUI/AutocompleteWrapper';
import AutocompleteItemForItem from '../../Controls/FormsUI/AutocompleteItemForItem';
import ButtonAction from '../../Controls/Buttons/ButtonAction';
import ButtonBasic from '../../Controls/Buttons/ButtonBasic';
import ButtonDelete from '../../Controls/Buttons/ButtonDelete';
import Contact from '../Contact/Contact';
import ContractTerm from '../Term/ContractTerm';
import EmailBasic from '../Email/EmailBasic';
import { initialValuesContact } from '../Tables/InitialValues/initialValuesContact';
import { initialValuesContractTerms } from '../Tables/InitialValues/initialValuesContractTerms';
import { initialValuesInv } from '../Tables/InitialValues/initialValuesInv';
import { initialValuesItemInv } from '../Tables/InitialValues/initialValuesItemInv';
import { initialValuesPerson } from '../Tables/InitialValues/initialValuesPerson';
import { initialValuesTermOfPayment } from '../Tables/InitialValues/initialValuesTermOfPayment';
import InputArray from '../../Controls/FormsUI/InputArray';
import InputArrayNumber from '../../Controls/FormsUI/InputArrayNumber';
import ItemInv from '../ItemInv/ItemInv';
import InvPdf from './InvPdf/InvPdf';
import LoadDetails from '../Load/LoadDetails';
import { maxNumberAllowedPerSubscription } from '../../Data/generalData';
import DesktopDatePickerBasic from '../../Controls/Inputs/DesktopDatePickerBasic';
import Popup from '../../Controls/Popups/Popup';
import SelectBasic from '../../Controls/FormsUI/SelectBasic';
import TermOfPayment from '../Term/TermOfPayment';
import TextFieldNormal from '../../Controls/Inputs/TextFieldNormal';
import TextFieldRegular from '../../Controls/Inputs/TextFieldRegular';
import TooltipBasic from '../../Controls/Inputs/TooltipBasic';
import { useGetCompanyContextQuery } from '../../../redux/features/companyApi';

//--------------------------------------- UTILS ---------------------------------------//

const MenuStyled = styled((props) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'top',
      horizontal: 'right',
    }}
    transformOrigin={{
      vertical: 'bottom',
      horizontal: 'right',
    }}
    {...props}
  />
))(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 6,
    marginTop: theme.spacing(0),
    minWidth: 180,

    color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],

    boxShadow:
      'rgb(255, 255, 255) 0px 0px 0px 0px, ' +
      'rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, ' +
      'rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, ' +
      'rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',

    '& .MuiMenu-list': {
      padding: '4px 0',
    },

    '& .MuiMenuItem-root': {
      '& .MuiSvgIcon-root': {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },

      '&:active': {
        backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
      },
    },
  },
}));

const TypographyPopover = styled(Typography)(({ theme }) => ({
  padding: 16,
  color: 'rgba(0, 0, 0, 0.87)',
  maxWidth: 350,
  fontSize: theme.typography.pxToRem(20),
}));

const isDirectImageUrl = (value) => typeof value === 'string' && /^(https?:\/\/|data:|blob:|\/)/i.test(value);

const resolveImageUrl = (value) => {
  if (!value) {
    return null;
  }

  if (isDirectImageUrl(value)) {
    return value;
  }

  if (typeof value !== 'object') {
    return null;
  }

  return value.s3_url || value.url || value.image || value.file || null;
};

const getImageQueryArg = (value) => {
  if (!value || resolveImageUrl(value)) {
    return '';
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  return String(value.id || value.uf || value.unique_field || '');
};

const getInvoiceIdentifier = (invoice) => {
  return invoice?.id ?? invoice?.uf ?? null;
};

const normalizeDateValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsedDate = dayjs(value);

  return parsedDate.isValid() ? parsedDate.format('YYYY-MM-DD') : null;
};

const normalizeInvoice = (value) => {
  const source = value && typeof value === 'object' ? value : {};

  const mergedInvoice = {
    ...initialValuesInv,
    ...source,
  };

  return {
    ...mergedInvoice,

    date_inv: normalizeDateValue(mergedInvoice.date_inv),

    date_due: normalizeDateValue(mergedInvoice.date_due),

    date_load: normalizeDateValue(mergedInvoice.date_load),

    date_unload: normalizeDateValue(mergedInvoice.date_unload),

    bill_to: mergedInvoice.bill_to ?? null,

    currency: mergedInvoice.currency ?? null,

    payment_term: mergedInvoice.payment_term ?? null,

    contract_terms: mergedInvoice.contract_terms ?? null,

    person: mergedInvoice.person ?? null,

    status: mergedInvoice.status ?? null,

    iteminv_invs: Array.isArray(mergedInvoice.iteminv_invs) ? mergedInvoice.iteminv_invs : [],

    comment_invs: Array.isArray(mergedInvoice.comment_invs) ? mergedInvoice.comment_invs : [],

    history_invs: Array.isArray(mergedInvoice.history_invs) ? mergedInvoice.history_invs : [],

    is_quote: false,
  };
};

const nullableDateSchema = () =>
  Yup.date()
    .transform((parsedValue, originalValue) => {
      if (originalValue === null || originalValue === undefined || originalValue === '') {
        return null;
      }

      const parsedDate = dayjs(originalValue);

      return parsedDate.isValid() ? parsedDate.toDate() : new Date('');
    })
    .nullable()
    .typeError(() =>
      i18n.t('yup.invalid_date', {
        defaultValue: 'Invalid date',
      })
    );

const validationSchema = Yup.object().shape({
  bill_to: Yup.object()
    .shape()
    .typeError(() => i18n.t('yup.required'))
    .required(() => i18n.t('yup.required')),

  vn: Yup.number()
    .lessThan(99999999, () => i18n.t('yup.invoice_number_max_length'))
    .positive(() => i18n.t('yup.invoice_number_should_be_positive'))
    .integer(() => i18n.t('yup.invoice_number_should_be_integer'))
    .typeError(() => i18n.t('yup.invoice_number_should_be_number'))
    .nullable(),

  date_inv: nullableDateSchema().required(() => i18n.t('yup.required')),

  date_due: nullableDateSchema(),

  currency: Yup.object()
    .shape()
    .typeError(() => i18n.t('yup.required'))
    .required(() => i18n.t('yup.required')),

  load_detail: Yup.string()
    .max(300, () => i18n.t('yup.max_300_characters'))
    .nullable(),

  load_address: Yup.string()
    .max(300, () => i18n.t('yup.max_300_characters'))
    .nullable(),

  unload_address: Yup.string()
    .max(300, () => i18n.t('yup.max_300_characters'))
    .nullable(),

  iteminv_invs: Yup.array().of(
    Yup.object().shape({
      item_for_item_inv: Yup.object()
        .shape()
        .typeError(() => i18n.t('yup.required'))
        .required(() => i18n.t('yup.required')),

      quantity: Yup.number()
        .lessThan(10000, () => i18n.t('yup.max_4_digits'))
        .typeError(() => i18n.t('yup.invoice_number_should_be_number'))
        .nullable(),

      amount: Yup.number()
        .lessThan(100000000, () => i18n.t('yup.max_8_digits'))
        .typeError(() => i18n.t('yup.invoice_number_should_be_number'))
        .nullable(),
    })
  ),

  comment_invs: Yup.array().of(
    Yup.object().shape({
      comment: Yup.string().max(400),
    })
  ),
});

//--------------------------------------- MAIN COMPONENT ---------------------------------------//

const Inv = forwardRef(({ initialValue = initialValuesInv, onSaved, onDeleted }, ref) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const theme = useTheme();

  const smallMedia = useMediaQuery('(max-width:1075px)');

  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));

  const mdMediaDown = useMediaQuery(theme.breakpoints.down('md'));

  const lgMediaDown = useMediaQuery(theme.breakpoints.down('lg'));

  const [checked, setChecked] = useState(false);

  const [showPdf, setShowPdf] = useState(false);

  const [formValues, setFormValues] = useState(null);

  const [disabledAddButtonClient, setDisabledAddButtonClient] = useState(false);

  const [disabledAddButtonItemInv, setDisabledAddButtonItemInv] = useState(false);

  const { data: account } = useRetrieveUserQuery();

  const {
    data: companyContext,
    isLoading: isCompanyLoading,
    isFetching: isCompanyFetching,
  } = useGetCompanyContextQuery();

  const company = companyContext?.company ?? null;

  const subscription_type = account?.subscription_type ?? null;

  const logoValue = company?.logo ?? null;

  const stampValue = company?.stamp ?? null;

  const logoQueryArg = getImageQueryArg(logoValue);

  const stampQueryArg = getImageQueryArg(stampValue);

  const { data: logoResponse } = useGetImageQuery(logoQueryArg, {
    skip: !logoQueryArg,
  });

  const { data: stampResponse } = useGetImageQuery(stampQueryArg, {
    skip: !stampQueryArg,
  });

  const logo = resolveImageUrl(logoValue) || resolveImageUrl(logoResponse);

  const stamp = resolveImageUrl(stampValue) || resolveImageUrl(stampResponse);

  const [invoice, setInvoice] = useState(() => normalizeInvoice(initialValue));

  const [createInv, { isLoading: isCreating }] = useCreateInvMutation();

  const [updateInv, { isLoading: isUpdating }] = useUpdateInvMutation();

  const [removeInv, { isLoading: isDeleting }] = useDeleteInvMutation();

  const isSaving = isCreating || isUpdating;

  const { contacts = [] } = useSelector((state) => state.contactSlice ?? {});

  const { bankAccounts = [] } = useSelector((state) => state.bankAccountSlice ?? {});

  const { itemsForItemInv = [] } = useSelector((state) => state.itemForItemInvSlice ?? {});

  const { itemForEdit = null } = useSelector((state) => state.itemForEditSlice ?? {});

  const [invoicePdfLang, setInvoicePdfLang] = useState('');

  const legalAddress = String(
    formValues && formValues.bill_to
      ? (formValues.bill_to.countrycodelegal ? formValues.bill_to.countrycodelegal.value : '') +
          (formValues.bill_to.zipcodelegal ? ` - ${formValues.bill_to.zipcodelegal}` : '') +
          (formValues.bill_to.citylegal ? `, ${formValues.bill_to.citylegal}` : '') +
          (formValues.bill_to.addresslegal ? `, ${formValues.bill_to.addresslegal}` : '')
      : ''
  );

  const freeAppMssg = subscription_type === 'active_free' ? t('note.invoice_created_with_online_app_cargos_md') : '';

  let totalInvoice = 0;

  ////// INITIAL VALUES EFFECT //////
  useEffect(() => {
    const nextInvoice = normalizeInvoice(initialValue);

    setInvoice(nextInvoice);
    setFormValues(nextInvoice);

    setChecked(Boolean(nextInvoice.load_detail || nextInvoice.load_address || nextInvoice.unload_address));

    setShowPdf(false);
  }, [initialValue]);

  useEffect(() => {
    if (
      (subscription_type === 'active_free' && contacts.length >= maxNumberAllowedPerSubscription[9]) ||
      (subscription_type === 'active_pro' && contacts.length >= maxNumberAllowedPerSubscription[10]) ||
      (subscription_type === 'active_premium' && contacts.length >= maxNumberAllowedPerSubscription[11])
    ) {
      setDisabledAddButtonClient(true);
    } else {
      setDisabledAddButtonClient(false);
    }

    if (
      (subscription_type === 'active_free' && itemsForItemInv.length >= maxNumberAllowedPerSubscription[12]) ||
      (subscription_type === 'active_pro' && itemsForItemInv.length >= maxNumberAllowedPerSubscription[13]) ||
      (subscription_type === 'active_premium' && itemsForItemInv.length >= maxNumberAllowedPerSubscription[14])
    ) {
      setDisabledAddButtonItemInv(true);
    } else {
      setDisabledAddButtonItemInv(false);
    }
  }, [contacts.length, itemsForItemInv.length, subscription_type]);

  const [anchorEl, setAnchorEl] = useState(null);

  const [anchorEl2, setAnchorEl2] = useState(null);

  const [anchorElHistory, setAnchorElHistory] = useState(null);

  const handleClickPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClickPopover2 = (event) => {
    setAnchorEl2(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setAnchorEl2(null);
    setAnchorElHistory(null);
  };

  const open = Boolean(anchorEl);

  const open2 = Boolean(anchorEl2);

  const popoverId = open ? 'client-popover-content' : undefined;

  const itemPopoverId = open2 ? 'item-popover-content' : undefined;

  const [anchorEl3, setAnchorEl3] = useState(null);

  const [anchorElEmail, setAnchorElEmail] = useState(null);

  const openSelectButton = Boolean(anchorEl3);

  const openSelectButtonEmail = Boolean(anchorElEmail);

  const handleClick = (event) => {
    setAnchorEl3(event.currentTarget);
  };

  const handleClickEmail = (event) => {
    setAnchorElEmail(event.currentTarget);
  };

  const handleCloseSelectMenu = () => {
    setAnchorEl3(null);
  };

  const handleCloseEmail = () => {
    setAnchorElEmail(null);
  };

  const ITEM_HEIGHT = 48;

  const openHistory = Boolean(anchorElHistory);

  const handleClickHistory = (event) => {
    setAnchorElHistory(event.currentTarget);
  };

  const FormValues = () => {
    const { values, touched } = useFormikContext();

    useEffect(() => {
      if (values) {
        setFormValues(values);
      }

      if (Object.keys(touched).length !== 0) {
        dispatch(setFormTouchedInv(true));
      }
    }, [touched, values]);

    return null;
  };

  const addOrEditAutoField = (value) => {
    if (!itemForEdit || !formValues) {
      return;
    }

    let updatedIteminvInvs = null;

    if (itemForEdit.fieldName === 'iteminv_invs') {
      const currentItems = Array.isArray(formValues.iteminv_invs) ? [...formValues.iteminv_invs] : [];

      const currentItem = currentItems[itemForEdit.index] ?? {};

      const newValue = {
        ...currentItem,
        item_for_item_inv: value,
      };

      updatedIteminvInvs = currentItems.map((itemInv, index) => {
        if (!itemInv.item_for_item_inv) {
          return {
            ...newValue,
          };
        }

        if (index === itemForEdit.index) {
          return {
            ...itemInv,
            item_for_item_inv: {
              ...value,
            },
          };
        }

        return {
          ...itemInv,
        };
      });
    }

    setInvoice({
      ...formValues,

      [itemForEdit.fieldName]: updatedIteminvInvs || value,
    });

    batch(() => {
      dispatch(setPopup(false));

      dispatch(setItemForEdit(null));
    });
  };

  const deleteAutoField = (id) => {
    if (!itemForEdit || !formValues) {
      return;
    }

    let updatedIteminvInvs = null;

    if (itemForEdit.fieldName === 'iteminv_invs') {
      updatedIteminvInvs = (formValues.iteminv_invs ?? []).filter((item) => item.item_for_item_inv?.id !== id);
    }

    setInvoice({
      ...formValues,

      [itemForEdit.fieldName]: updatedIteminvInvs || '',
    });

    dispatch(setItemForEdit(null));
  };

  const openInPopupAutoField = (item, fieldName, index = null) => {
    let fields = {};

    if (fieldName === 'bill_to' && !item) {
      fields = {
        ...initialValuesContact,
      };
    } else if (fieldName === 'person' && !item) {
      fields = {
        ...initialValuesPerson,
      };
    } else if (fieldName === 'payment_term' && !item) {
      fields = {
        ...initialValuesTermOfPayment,
      };
    } else if (fieldName === 'contract_terms' && !item) {
      fields = {
        ...initialValuesContractTerms,
      };
    } else if (fieldName === 'iteminv_invs' && !item) {
      fields = {
        ...initialValuesItemInv,
      };
    } else {
      fields = {
        ...(item ?? {}),
      };
    }

    batch(() => {
      dispatch(setPopup(true));

      dispatch(
        setItemForEdit({
          ...fields,
          fieldName,
          index,
        })
      );
    });
  };

  const buildInvoicePayload = () => ({
    vn: formValues.vn,
    date_inv: formValues.date_inv,
    date_due: formValues.date_due,
    bill_to: formValues.bill_to || null,
    currency: formValues.currency?.currency_code ?? null,
    load_detail: checked ? formValues.load_detail : '',
    load_address: checked ? formValues.load_address : '',
    unload_address: checked ? formValues.unload_address : '',
    date_load: checked ? formValues.date_load : '',
    date_unload: checked ? formValues.date_unload : '',
    load: '',
    iteminv_invs: formValues.iteminv_invs,
    comment_invs: formValues.comment_invs,
    payment_term: formValues.payment_term,
    contract_terms: formValues.contract_terms,
    person: formValues.person,
    status: formValues.status,
    is_quote: false,
  });

  const showSaveError = (error) => {
    const responseData = JSON.stringify(error?.data ?? '');

    if (error?.status === 402) {
      toast.error(() => t('toast.need_to_upgrade_in_order_to_register_new_invoices'), {
        autoClose: 4000,
      });

      return;
    }

    if (responseData.includes('not unique')) {
      toast.error(() => t('toast.invoice_with_this_number_exist'));

      return;
    }

    toast.error(() => t('toast.not_saved_try_again'));
  };

  const saveInvoice = async ({ closeAfterSave }) => {
    if (!formValues || isSaving) {
      return;
    }

    const invoiceIdentifier = getInvoiceIdentifier(formValues);

    const wasUpdate = invoiceIdentifier !== null && invoiceIdentifier !== undefined && invoiceIdentifier !== '';

    const payload = buildInvoicePayload();

    try {
      const savedInvoice = wasUpdate
        ? await updateInv({
            id: invoiceIdentifier,
            body: payload,
          }).unwrap()
        : await createInv(payload).unwrap();

      dispatch(setFormTouchedInv(false));

      onSaved?.(savedInvoice);

      toast.success(
        `${t('toast.invoice')} # ${savedInvoice?.vn ?? ''} ${
          wasUpdate ? t('toast.successfully_updated') : t('toast.successfully_created')
        }`
      );

      if (closeAfterSave) {
        setFormValues(null);

        setInvoice(normalizeInvoice(initialValuesInv));

        setChecked(false);

        batch(() => {
          dispatch(setPopupInv(false));

          dispatch(setPopupInv2(false));
        });

        return;
      }

      const normalizedInvoice = normalizeInvoice({
        ...savedInvoice,

        uf: savedInvoice?.uf ?? formValues?.uf ?? null,

        id: savedInvoice?.id ?? formValues?.id ?? undefined,
      });

      setInvoice(normalizedInvoice);

      setFormValues(normalizedInvoice);

      setChecked(
        Boolean(normalizedInvoice.load_detail || normalizedInvoice.load_address || normalizedInvoice.unload_address)
      );
    } catch (error) {
      console.error('Invoice save failed:', error);

      showSaveError(error);
    }
  };

  const handleSubmit = () =>
    saveInvoice({
      closeAfterSave: true,
    });

  const handleSave = (event) => {
    event.preventDefault();

    setShowPdf(false);

    return saveInvoice({
      closeAfterSave: false,
    });
  };

  const handleDelete = async (_, id) => {
    if (!id || isDeleting) {
      toast.error(() => t('toast.invoice_could_not_be_found'));

      return;
    }

    try {
      await removeInv(id).unwrap();

      const invoiceNumber = formValues?.vn || invoice?.vn || '';

      setFormValues(null);

      setInvoice({
        ...initialValuesInv,
      });

      setChecked(false);

      batch(() => {
        dispatch(setPopupInv(false));

        dispatch(setPopupInv2(false));

        dispatch(setFormTouchedInv(false));
      });

      onDeleted?.(id);

      toast.success(`${t('toast.invoice')} # ${invoiceNumber} ${t('toast.successfully_deleted')}`);
    } catch (error) {
      console.error('Invoice delete failed', error);

      toast.error(() => t('toast.invoice_could_not_be_deleted'));
    }
  };

  useImperativeHandle(ref, () => ({
    handleSubmit,
  }));

  const handleSwitchChange = () => {
    setChecked((current) => !current);
  };

  const generatePdfDocument = (lang) => {
    setShowPdf(true);
    setInvoicePdfLang(lang);

    handleClose();

    return null;
  };

  const emailPdfDocument = (values, _type, currentTotal, lang) => {
    batch(() => {
      dispatch(
        setItemForEdit({
          ...values,

          fieldName: 'email',

          totalInvoice: currentTotal,
        })
      );

      dispatch(setPopup(true));
    });

    setInvoicePdfLang(lang);

    handleCloseEmail();

    return null;
  };

  const validateAndRun = async (validateForm, setTouched, callback) => {
    const errors = await validateForm();

    if (Object.keys(errors).length > 0) {
      toast.error(() => t('toast.correct_errors_on_page'));

      await setTouched(setNestedObjectValues(errors, true));

      return;
    }

    await callback();
  };

  console.log('INV RENDER:', company);

  return (
    <>
      <Container
        sx={{
          width: '100%',
        }}
        maxWidth={false}
        disableGutters={lgMediaDown}
      >
        <Formik
          enableReinitialize
          initialValues={invoice}
          validateOnChange={false}
          validateOnBlur={false}
          validationSchema={validationSchema}
        >
          {({ values, errors, validateForm, setTouched, setFieldValue }) => {
            const itemRows = Array.isArray(values.iteminv_invs) ? values.iteminv_invs : [];

            const subtotal = itemRows.reduce((total, item) => {
              const amount = Number(item.amount) || 0;

              const quantity = typeof item.quantity === 'number' ? item.quantity : Number(item.quantity) || 1;

              return total + amount * quantity;
            }, 0);

            const vatTotal = itemRows.reduce((total, item) => {
              const amount = Number(item.amount) || 0;

              const quantity = typeof item.quantity === 'number' ? item.quantity : Number(item.quantity) || 1;

              const vat = Number(item.item_for_item_inv?.vat) || 0;

              return total + amount * quantity * (vat / 100);
            }, 0);

            totalInvoice = (subtotal + vatTotal).toFixed(2);

            return (
              <Form autoComplete="off">
                <Grid
                  container
                  sx={{
                    justifyContent: 'flex-end',
                  }}
                >
                  {Object.keys(errors).length !== 0 && (
                    <Typography color="error">{t('yup.fields_color_red_are_mandatory')}</Typography>
                  )}
                </Grid>

                <Grid
                  container
                  spacing={1}
                  sx={{
                    mt: 3,
                    mb: 1,
                  }}
                >
                  <Grid
                    container
                    spacing={1}
                    sx={{
                      mb: 2,
                    }}
                  >
                    <Grid item xs={12}>
                      <Stack direction="row">
                        <Typography
                          variant="h4"
                          textAlign="start"
                          sx={{
                            ml: 1,
                          }}
                        >
                          {t('inv.invoice_to')}
                        </Typography>

                        <Typography
                          variant="h4"
                          sx={{
                            pl: 1,
                          }}
                        >
                          *
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Grid container item md={6.5} xs={12}>
                    <Grid container item md={10} xs={12}>
                      <Grid
                        item
                        sx={{
                          width: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                          }}
                        >
                          <Box
                            sx={{
                              flexGrow: 1,
                            }}
                          >
                            <AutocompleteWrapper name="bill_to" />
                          </Box>

                          <Box>
                            <IconButton
                              id="client-popover"
                              color="primary"
                              sx={{
                                mx: 0,
                                mt: -1,
                              }}
                              onClick={
                                disabledAddButtonClient && values.bill_to === null
                                  ? handleClickPopover
                                  : () => openInPopupAutoField(values.bill_to, 'bill_to')
                              }
                            >
                              <ModeEditOutlineIcon fontSize="small" />
                            </IconButton>

                            <Popover
                              id={popoverId}
                              open={open}
                              anchorEl={anchorEl}
                              onClose={handleClose}
                              anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                              }}
                            >
                              <TypographyPopover>
                                {t('toast.need_to_upgrade_in_order_to_register_new_clients')}
                              </TypographyPopover>
                            </Popover>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    <Grid container item xs={12}>
                      <Grid
                        item
                        xs={12}
                        sx={{
                          mt: 1,

                          display: smallMedia ? 'inline' : 'inline-flex',
                        }}
                      >
                        <Box
                          sx={{
                            display: smallMedia ? 'inline' : 'inline-flex',

                            minWidth: smallMedia ? undefined : 180,
                          }}
                        >
                          <Typography
                            display="inline"
                            variant="body1"
                            sx={{
                              fontWeight: '500',
                            }}
                          >
                            {t('inv.legal_address')}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'inline',

                            ml: smallMedia ? 1 : undefined,
                          }}
                        >
                          <Typography display="inline">{legalAddress}</Typography>
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sx={{
                          mt: 1,

                          display: smallMedia ? 'inline' : 'inline-flex',
                        }}
                      >
                        <Box
                          sx={{
                            display: smallMedia ? 'inline' : 'inline-flex',

                            minWidth: smallMedia ? undefined : 180,
                          }}
                        >
                          <Typography
                            display="inline"
                            variant="body1"
                            sx={{
                              fontWeight: '500',
                            }}
                          >
                            {t('inv.fiscal_code')}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'inline',

                            ml: smallMedia ? 1 : undefined,
                          }}
                        >
                          <Typography display="inline">{values.bill_to?.fiscalcode || ''}</Typography>
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sx={{
                          mt: 1,

                          display: smallMedia ? 'inline' : 'inline-flex',
                        }}
                      >
                        <Box
                          sx={{
                            display: smallMedia ? 'inline' : 'inline-flex',

                            minWidth: smallMedia ? undefined : 180,
                          }}
                        >
                          <Typography
                            display="inline"
                            variant="body1"
                            sx={{
                              fontWeight: '500',
                            }}
                          >
                            {t('inv.vat_code')}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'inline',

                            ml: smallMedia ? 1 : undefined,
                          }}
                        >
                          <Typography display="inline">{values.bill_to?.vatcode || ''}</Typography>
                        </Box>
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        sx={{
                          mt: 1,

                          display: smallMedia ? 'inline' : 'inline-flex',
                        }}
                      >
                        <Box
                          sx={{
                            display: smallMedia ? 'inline' : 'inline-flex',

                            minWidth: smallMedia ? undefined : 180,
                          }}
                        >
                          <Typography
                            display="inline"
                            variant="body1"
                            sx={{
                              fontWeight: '500',
                            }}
                          >
                            {t('inv.phone_email')}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: 'inline',

                            ml: smallMedia ? 1 : undefined,
                          }}
                        >
                          <Typography display="inline">
                            {values.bill_to?.phone || ''}

                            {values.bill_to?.phone && values.bill_to?.email1
                              ? ` / ${values.bill_to.email1}`
                              : !values.bill_to?.phone && values.bill_to?.email1
                                ? values.bill_to.email1
                                : ''}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Grid
                      container
                      item
                      md={10}
                      xs={12}
                      sx={{
                        mt: 2,

                        display: mdMediaDown ? 'inline' : 'inline-flex',
                      }}
                    >
                      <Box
                        sx={{
                          minWidth: smallMedia ? undefined : 180,

                          mt: 1.2,
                          pr: 1,
                        }}
                      >
                        <Typography variant="h6" textAlign="start" display="inline">
                          {t('quote.status')}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: mdMediaDown ? 'flex' : 'inline-flex',

                          flexGrow: mdMediaDown ? undefined : 1,

                          mt: 1.2,
                        }}
                      >
                        <Box
                          sx={{
                            display: mdMediaDown ? undefined : 'inline',

                            flexGrow: 1,
                          }}
                        >
                          <SelectBasic name="status" quoteorinv="1" />
                        </Box>

                        <Box>
                          <TooltipBasic title={t('notification.history')} placement="bottom-start">
                            <IconButton
                              id="status-button"
                              aria-controls={openHistory ? 'status-menu' : undefined}
                              aria-haspopup="true"
                              aria-expanded={openHistory ? 'true' : undefined}
                              color="inherit"
                              sx={{
                                mx: 0,
                                pt: 0,
                              }}
                              onClick={handleClickHistory}
                            >
                              <Badge badgeContent={values.history_invs?.length || 0}>
                                <AutoAwesomeMotionIcon />
                              </Badge>
                            </IconButton>
                          </TooltipBasic>
                        </Box>
                      </Box>

                      <Menu
                        id="status-menu"
                        aria-labelledby="status-button"
                        anchorEl={anchorElHistory}
                        open={openHistory}
                        onClose={handleClose}
                        PaperProps={{
                          elevation: 2,

                          sx: {
                            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',

                            mt: 2,
                          },

                          style: {
                            maxHeight: ITEM_HEIGHT * 9.5,

                            minWidth: 300,

                            maxWidth: 650,
                          },
                        }}
                        transformOrigin={{
                          horizontal: 'right',

                          vertical: 'top',
                        }}
                        anchorOrigin={{
                          horizontal: 'right',

                          vertical: 'bottom',
                        }}
                      >
                        {(values.history_invs ?? []).length < 1 ? (
                          <MenuItem>
                            <Typography noWrap>{t('notification.no_history')}</Typography>
                          </MenuItem>
                        ) : (
                          <MenuItem>
                            <Typography
                              sx={{
                                fontWeight: 'bold',
                              }}
                            >
                              {t('notification.history')}
                            </Typography>
                          </MenuItem>
                        )}

                        {(values.history_invs ?? [])
                          .slice()
                          .reverse()
                          .map((history, index) => (
                            <MenuItem key={history.id ?? index + 350}>
                              <Typography component="div" noWrap>
                                <Stack direction="row">
                                  <Box>
                                    {new Date(history.date_registered).toLocaleDateString('en-GB', {
                                      day: 'numeric',
                                      month: 'numeric',
                                      year: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })}{' '}
                                    {history.action ? t(`status.${history.action}`) : ''}
                                  </Box>

                                  <Box
                                    sx={{
                                      fontWeight: 'bold',

                                      mx: 1,
                                    }}
                                  >
                                    {history.status ? t(`status.${history.status}`) : ''}
                                  </Box>

                                  <Box
                                    sx={{
                                      whiteSpace: 'nowrap',

                                      overflow: 'hidden',

                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {history.changed_by
                                      ? history.changed_by.first_name || history.changed_by.last_name
                                        ? `${history.changed_by.first_name || ''} ${
                                            history.changed_by.last_name || ''
                                          }`.trim()
                                        : history.changed_by.email || ''
                                      : ''}
                                  </Box>
                                </Stack>
                              </Typography>
                            </MenuItem>
                          ))}
                      </Menu>
                    </Grid>
                  </Grid>

                  <Grid container item md={5.5} xs={12} spacing={2}>
                    <Grid container item xs={12} spacing={1}>
                      <Grid item md={6} xs={12}>
                        <Typography variant="h6" textAlign={mdMediaDown ? 'start' : 'end'}>
                          {t('inv.invoice_hash')}
                        </Typography>
                      </Grid>

                      <Grid item md={6} xs={12}>
                        <FastField
                          as={TextFieldNormal}
                          name="vn"
                          type="number"
                          placeholder={t('inv.number_will_be_generated')}
                        />
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} spacing={1}>
                      <Grid item md={6} xs={12}>
                        <Typography variant="h6" textAlign={mdMediaDown ? 'start' : 'end'}>
                          {t('inv.invoice_date')}
                        </Typography>
                      </Grid>

                      <Grid item md={6} xs={12}>
                        <DesktopDatePickerBasic name="date_inv" />
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} spacing={1}>
                      <Grid item md={6} xs={12}>
                        <Typography variant="h6" textAlign={mdMediaDown ? 'start' : 'end'}>
                          {t('inv.due_date')}
                        </Typography>
                      </Grid>

                      <Grid item md={6} xs={12}>
                        <DesktopDatePickerBasic name="date_due" />
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} spacing={1}>
                      <Grid item md={6} xs={12}>
                        <Typography variant="h6" textAlign={mdMediaDown ? 'start' : 'end'}>
                          {t('inv.payment_term')}
                        </Typography>
                      </Grid>

                      <Grid
                        item
                        md={6}
                        xs={12}
                        sx={{
                          width: '100%',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                          }}
                        >
                          <Box
                            sx={{
                              flexGrow: 1,
                            }}
                          >
                            <AutocompleteWrapper name="payment_term" />
                          </Box>

                          <Box>
                            <IconButton
                              color="primary"
                              sx={{
                                mx: 0,
                                mt: -1,
                              }}
                              onClick={() => openInPopupAutoField(values.payment_term, 'payment_term')}
                            >
                              <ModeEditOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    <Grid container item xs={12} spacing={1}>
                      <Grid item md={6} xs={12}>
                        <Typography variant="h6" textAlign={mdMediaDown ? 'start' : 'end'}>
                          {t('inv.currency_asterisk')}
                        </Typography>
                      </Grid>

                      <Grid item md={6} xs={12}>
                        <AutocompleteWrapper name="currency" />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <hr />
                  </Grid>

                  <FormGroup
                    sx={{
                      ml: 1.5,
                      mt: 1,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={handleSwitchChange}
                          inputProps={{
                            'aria-label': 'controlled',
                          }}
                        />
                      }
                      label={t('inv.include_load_details')}
                      componentsProps={{
                        typography: {
                          variant: 'button',

                          color: 'primary',
                        },
                      }}
                    />
                  </FormGroup>

                  {checked && (
                    <Grid container item spacing={1}>
                      <LoadDetails />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <hr />
                  </Grid>

                  <Grid
                    container
                    item
                    sx={{
                      mt: mdMediaDown ? undefined : 1,
                    }}
                  >
                    <FieldArray name="iteminv_invs">
                      {(itemArrayHelpers) => (
                        <>
                          {!mdMediaDown ? (
                            <Box
                              sx={{
                                display: 'flex',

                                width: '100%',
                              }}
                            >
                              <Box
                                sx={{
                                  flexGrow: 1,
                                  width: '100%',
                                }}
                              >
                                <Grid
                                  container
                                  xs={12}
                                  sx={{
                                    mt: 1,
                                    mb: 1,
                                  }}
                                >
                                  <Grid
                                    item
                                    md={6.5}
                                    xs={12}
                                    sx={{
                                      bgcolor: '#ccc',
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        ml: 1.5,
                                        fontWeight: '600',
                                        color: 'black',
                                      }}
                                      noWrap
                                    >
                                      {t('inv.description')}
                                    </Typography>
                                  </Grid>

                                  <Grid
                                    item
                                    xs={1}
                                    sx={{
                                      bgcolor: '#ccc',
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        ml: 1.5,
                                        fontWeight: '600',
                                        color: 'black',
                                      }}
                                      noWrap
                                    >
                                      {t('inv.quantity')}
                                    </Typography>
                                  </Grid>

                                  <Grid
                                    item
                                    xs={1.5}
                                    sx={{
                                      bgcolor: '#ccc',
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        ml: 1.5,
                                        fontWeight: '600',
                                        color: 'black',
                                      }}
                                    >
                                      {t('inv.rate')}
                                    </Typography>
                                  </Grid>

                                  <Grid
                                    item
                                    xs={1}
                                    sx={{
                                      bgcolor: '#ccc',
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        ml: 1.5,
                                        fontWeight: '600',
                                        color: 'black',
                                      }}
                                      noWrap
                                    >
                                      {t('inv.vat_%')}
                                    </Typography>
                                  </Grid>

                                  <Grid
                                    item
                                    xs={2}
                                    sx={{
                                      bgcolor: '#ccc',
                                    }}
                                  >
                                    <Typography
                                      align="right"
                                      marginRight={2}
                                      sx={{
                                        ml: 1.5,
                                        fontWeight: '600',
                                        color: 'black',
                                      }}
                                    >
                                      {t('inv.amount')}
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </Box>

                              <Box
                                sx={{
                                  alignSelf: 'start',

                                  minWidth: 31.8,
                                }}
                              />
                            </Box>
                          ) : (
                            <Grid
                              item
                              xs={12}
                              sx={{
                                mb: 1.5,
                              }}
                            >
                              <Typography variant="h6" textAlign="start">
                                {t('general.goods_services_capital')}
                              </Typography>
                            </Grid>
                          )}

                          {itemRows.map((item, index) => {
                            const amount = Number(item.amount) || 0;

                            const quantity =
                              typeof item.quantity === 'number' ? item.quantity : Number(item.quantity) || 1;

                            const vat = Number(item.item_for_item_inv?.vat) || 0;

                            const articleTotal =
                              Math.round(amount * (1 + vat / 100) * quantity * 100 + Number.EPSILON) / 100;

                            return (
                              <div
                                key={item.id ?? index}
                                style={{
                                  width: '100%',
                                }}
                              >
                                <Box
                                  sx={{
                                    display: mdMediaDown ? 'inline' : 'flex',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      flexGrow: 1,
                                      width: '100%',
                                    }}
                                  >
                                    <Grid container xs={12}>
                                      <Grid
                                        item
                                        md={6.5}
                                        xs={12}
                                        sx={{
                                          width: '100%',
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              flexGrow: 1,
                                            }}
                                          >
                                            <AutocompleteItemForItem
                                              name={`iteminv_invs[${index}].item_for_item_inv`}
                                              label={mdMediaDown ? t('inv.description') : null}
                                            />
                                          </Box>

                                          <Box
                                            sx={{
                                              mt: mdMediaDown ? 2 : -1,
                                            }}
                                          >
                                            <IconButton
                                              id={`item-inv-popover-${index}`}
                                              color="primary"
                                              onClick={
                                                disabledAddButtonItemInv && item.item_for_item_inv === null
                                                  ? handleClickPopover2
                                                  : () =>
                                                      openInPopupAutoField(
                                                        item.item_for_item_inv,
                                                        'iteminv_invs',
                                                        index
                                                      )
                                              }
                                            >
                                              <ModeEditOutlineIcon fontSize="small" />
                                            </IconButton>

                                            <Popover
                                              id={itemPopoverId}
                                              open={open2}
                                              anchorEl={anchorEl2}
                                              onClose={handleClose}
                                              anchorOrigin={{
                                                vertical: 'bottom',

                                                horizontal: 'right',
                                              }}
                                            >
                                              <TypographyPopover>
                                                {t('toast.need_to_upgrade_in_order_to_register_new_items_for_item_inv')}
                                              </TypographyPopover>
                                            </Popover>
                                          </Box>
                                        </Box>
                                      </Grid>

                                      <Grid item md={1} xs={12}>
                                        <InputArrayNumber
                                          name={`iteminv_invs[${index}].quantity`}
                                          type="number"
                                          label={mdMediaDown ? t('inv.quantity') : null}
                                        />
                                      </Grid>

                                      <Grid item md={1.5} xs={12}>
                                        <InputArrayNumber
                                          name={`iteminv_invs[${index}].amount`}
                                          type="number"
                                          label={mdMediaDown ? t('inv.rate') : null}
                                        />
                                      </Grid>

                                      <Grid item md={1} xs={12}>
                                        <Field
                                          component={InputArray}
                                          name={`iteminv_invs[${index}].item_for_item_inv.vat`}
                                          type="number"
                                          disabled
                                          label={mdMediaDown ? t('inv.vat_%') : null}
                                        />
                                      </Grid>

                                      <Grid
                                        container
                                        item
                                        md={2}
                                        xs={12}
                                        sx={{
                                          mt: mdMediaDown ? 0 : -1,
                                        }}
                                      >
                                        <Field
                                          name={`iteminv_invs[${index}].article_total`}
                                          component={TextFieldRegular}
                                          type="number"
                                          label={mdMediaDown ? t('inv.total') : null}
                                          inputProps={{
                                            style: {
                                              textAlign: mdMediaDown ? 'start' : 'end',
                                            },
                                          }}
                                          value={articleTotal || ''}
                                          onChange={(event) => {
                                            const enteredTotal = Number(event.target.value) || 0;

                                            const vatMultiplier = 1 + vat / 100;

                                            const quantityValue = quantity || 1;

                                            const baseAmount = enteredTotal / vatMultiplier / quantityValue;

                                            void setFieldValue(`iteminv_invs[${index}].amount`, baseAmount);
                                          }}
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>

                                  <Box
                                    sx={{
                                      alignSelf: 'start',

                                      display: mdMediaDown ? 'inline' : undefined,
                                    }}
                                  >
                                    <ButtonAction
                                      type="button"
                                      fullWidth={mdMediaDown}
                                      disabled={itemRows.length < 2}
                                      size="small"
                                      color="secondary"
                                      variant={mdMediaDown ? 'contained' : 'text'}
                                      sx={{
                                        mb: mdMediaDown ? 2 : 2.5,
                                      }}
                                      style={{
                                        maxWidth: mdMediaDown ? undefined : '32px',

                                        maxHeight: mdMediaDown ? undefined : '20px',

                                        minWidth: mdMediaDown ? undefined : '32px',

                                        minHeight: mdMediaDown ? undefined : '20px',
                                      }}
                                      onClick={() => itemArrayHelpers.remove(index)}
                                    >
                                      <RemoveIcon />
                                    </ButtonAction>
                                  </Box>
                                </Box>

                                {mdMediaDown && itemRows.length !== index + 1 && (
                                  <Grid item xs={12}>
                                    <Divider variant="middle" />
                                  </Grid>
                                )}
                              </div>
                            );
                          })}

                          <Grid container>
                            <Grid item xs={12}>
                              <ButtonBasic
                                type="button"
                                color="primary"
                                variant="outlined"
                                fullWidth={smMediaDown}
                                disabled={itemRows.length > 4}
                                style={{
                                  marginTop: 0,
                                  marginBottom: 0,
                                }}
                                onClick={() =>
                                  itemArrayHelpers.push({
                                    item_for_item_inv: null,

                                    description: '',

                                    quantity: null,

                                    amount: null,

                                    vat: null,
                                  })
                                }
                              >
                                <Typography noWrap variant="button">
                                  {t('button.add_line_item')}
                                </Typography>
                              </ButtonBasic>
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </FieldArray>
                  </Grid>

                  <Grid
                    container
                    item
                    xs={12}
                    sx={{
                      mb: 1,
                      mt: 0,
                    }}
                  >
                    <Grid item md={4} xs={0} />

                    <Grid
                      item
                      md={5}
                      xs={7}
                      sx={{
                        pr: 1,
                      }}
                    >
                      <Stack spacing={2}>
                        <Typography variant="h6" align="right">
                          {t('inv.sub_total')}
                        </Typography>

                        <Typography variant="h6" align="right">
                          {t('inv.vat')}
                        </Typography>

                        <Divider variant="middle" />

                        <Typography
                          variant="h6"
                          align="right"
                          sx={{
                            fontWeight: '700',
                          }}
                        >
                          {t('inv.total')}
                        </Typography>
                      </Stack>
                    </Grid>

                    <Grid item md={2.5} xs={4.5}>
                      <Stack spacing={2} marginRight={2}>
                        <Typography variant="h6" align="right">
                          {subtotal.toFixed(2)}
                        </Typography>

                        <Typography variant="h6" align="right">
                          {vatTotal.toFixed(2)}
                        </Typography>

                        <Divider variant="middle" />

                        <Typography
                          variant="h6"
                          align="right"
                          sx={{
                            fontWeight: '700',
                          }}
                        >
                          {totalInvoice}
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <hr />
                  </Grid>

                  <Grid container item spacing={1}>
                    <FieldArray name="comment_invs">
                      {(commentArrayHelpers) => (
                        <>
                          {(values.comment_invs ?? []).map((comment, index) => (
                            <Grid
                              item
                              xs={12}
                              key={comment.id ?? index}
                              sx={{
                                width: '100%',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                }}
                              >
                                <Box
                                  sx={{
                                    flexGrow: 1,
                                  }}
                                >
                                  <FastField
                                    component={InputArray}
                                    name={`comment_invs[${index}].comment`}
                                    label={t('inv.note_to_invoice')}
                                    type="text"
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxRows={4}
                                    helperText={errors.comment_invs?.[index]?.comment}
                                  />
                                </Box>

                                <Box
                                  sx={{
                                    alignSelf: 'center',
                                  }}
                                >
                                  <IconButton
                                    type="button"
                                    onClick={() => commentArrayHelpers.remove(index)}
                                    color="secondary"
                                  >
                                    <RemoveIcon />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Grid>
                          ))}

                          <Grid
                            container
                            item
                            xs={12}
                            sx={{
                              pt: 0,
                            }}
                          >
                            <ButtonBasic
                              type="button"
                              size="small"
                              color="primary"
                              variant="outlined"
                              fullWidth={smMediaDown}
                              disabled={(values.comment_invs ?? []).length > 2}
                              style={{
                                marginTop: 0,
                              }}
                              onClick={() =>
                                commentArrayHelpers.push({
                                  comment: '',
                                })
                              }
                            >
                              <Typography noWrap variant="button">
                                {t('button.add_note_to_invoice')}
                              </Typography>
                            </ButtonBasic>
                          </Grid>
                        </>
                      )}
                    </FieldArray>
                  </Grid>

                  <Grid container item spacing={1}>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                        }}
                      >
                        <Box
                          sx={{
                            flexGrow: 1,
                          }}
                        >
                          <AutocompleteWrapper name="contract_terms" label={t('inv.invoice_terms')} />
                        </Box>

                        <Box>
                          <IconButton
                            color="primary"
                            sx={{
                              mt: 2.5,
                            }}
                            onClick={() => openInPopupAutoField(values.contract_terms, 'contract_terms')}
                          >
                            <ModeEditOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>

                  <Grid item xs={12}>
                    <hr />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    sx={{
                      my: 3,

                      justifyContent: mdMediaDown ? 'center' : 'start',
                    }}
                  >
                    <Stack direction={smallMedia ? 'column' : 'row'}>
                      <Stack direction={smMediaDown ? 'column' : 'row'}>
                        <ButtonBasic
                          type="button"
                          disabled={isSaving || isDeleting}
                          variant="outlined"
                          fullWidth={smMediaDown}
                          startIcon={<SaveAsIcon />}
                          onClick={() => validateAndRun(validateForm, setTouched, handleSubmit)}
                        >
                          <Typography variant="button">{t('button.save_and_close')}</Typography>
                        </ButtonBasic>

                        <ButtonBasic
                          type="button"
                          disabled={isSaving || isDeleting}
                          fullWidth={smMediaDown}
                          variant="outlined"
                          startIcon={<SaveIcon />}

                          onClick={(event) => validateAndRun(validateForm, setTouched, () => handleSave(event))}
                        >
                          <Typography variant="button">{t('button.save')}</Typography>
                        </ButtonBasic>

                        <ButtonDelete
                          id={getInvoiceIdentifier(values)}
                          type="button"
                          disabled={isSaving || isDeleting || !getInvoiceIdentifier(values)}
                          variant="outlined"
                          color="secondary"
                          fullWidth={smMediaDown}

                          handleDelete={handleDelete}
                        >
                          <Typography variant="button">{t('button.delete')}</Typography>
                        </ButtonDelete>
                      </Stack>

                      <Stack direction={smMediaDown ? 'column' : 'row'}>
                        <ButtonBasic
                          id="invoice-email-button"
                          variant="outlined"
                          color="success"
                          fullWidth={smMediaDown}
                          startIcon={smallMedia ? <ForwardToInboxIcon /> : <PictureAsPdfIcon />}
                          disabled={!values.uf}
                          sx={{
                            ml: smallMedia ? null : 2,
                          }}
                          aria-controls={openSelectButtonEmail ? 'invoice-email-menu' : undefined}
                          aria-haspopup="true"
                          aria-expanded={openSelectButtonEmail ? 'true' : undefined}
                          disableElevation
                          onClick={handleClickEmail}
                          endIcon={<KeyboardArrowDownIcon />}
                        >
                          <Typography variant="button">{t('button.email')}</Typography>
                        </ButtonBasic>

                        <MenuStyled
                          id="invoice-email-menu"
                          MenuListProps={{
                            'aria-labelledby': 'invoice-email-button',
                          }}
                          anchorEl={anchorElEmail}
                          open={openSelectButtonEmail}
                          onClose={handleCloseEmail}
                        >
                          <MenuItem
                            type="button"
                            disableRipple
                            onClick={() =>
                              validateAndRun(validateForm, setTouched, () =>
                                emailPdfDocument(values, 'email', totalInvoice, 'RO/RU')
                              )
                            }
                          >
                            <PictureAsPdfIcon />

                            <Typography>{t('general.ro_ru')}</Typography>
                          </MenuItem>

                          <MenuItem
                            type="button"
                            disableRipple
                            onClick={() =>
                              validateAndRun(validateForm, setTouched, () =>
                                emailPdfDocument(values, 'email', totalInvoice, 'EN')
                              )
                            }
                          >
                            <PictureAsPdfIcon />

                            <Typography>{t('general.en')}</Typography>
                          </MenuItem>
                        </MenuStyled>

                        <ButtonBasic
                          id="invoice-pdf-button"
                          variant="outlined"
                          color={showPdf ? 'error' : 'success'}
                          startIcon={<PictureAsPdfIcon />}
                          disabled={!values.uf || isCompanyLoading || isCompanyFetching || !company}
                          fullWidth={smMediaDown}
                          endIcon={<KeyboardArrowDownIcon />}
                          onClick={handleClick}
                        >
                          <Typography
                            variant="button"
                            sx={{
                              display: showPdf ? 'none' : 'inline',
                            }}
                          >
                            {t('button.generate')}
                          </Typography>

                          {showPdf && <Typography variant="button">{t('general.download')}</Typography>}
                        </ButtonBasic>

                        <MenuStyled
                          id="invoice-pdf-menu"
                          MenuListProps={{
                            'aria-labelledby': 'invoice-pdf-button',
                          }}
                          anchorEl={anchorEl3}
                          open={openSelectButton}
                          onClose={handleCloseSelectMenu}
                        >
                          <MenuItem
                            type="button"
                            disableRipple
                            onClick={() => validateAndRun(validateForm, setTouched, () => generatePdfDocument('RO/RU'))}
                          >
                            <PictureAsPdfIcon
                              sx={{
                                display: showPdf ? 'none' : 'inline-flex',
                              }}
                            />

                            <Typography
                              sx={{
                                display: showPdf ? 'none' : 'block',
                              }}
                            >
                              {t('general.ro_ru')}
                            </Typography>

                            {showPdf && invoicePdfLang === 'RO/RU' && (
                              <InvPdf
                                account={account}
                                company={company}
                                values={values}
                                bankAccounts={bankAccounts}
                                logo={logo}
                                stamp={stamp}
                                update
                                invoicePdfLang={invoicePdfLang}
                                setShowPdf={setShowPdf}
                                showPdf={showPdf}
                                checked={checked}
                                freeAppMssg={freeAppMssg}
                              />
                            )}
                          </MenuItem>

                          <MenuItem
                            type="button"
                            disableRipple
                            onClick={() => validateAndRun(validateForm, setTouched, () => generatePdfDocument('EN'))}
                          >
                            <PictureAsPdfIcon
                              sx={{
                                display: showPdf ? 'none' : 'inline-flex',
                              }}
                            />

                            <Typography
                              sx={{
                                display: showPdf ? 'none' : 'block',
                              }}
                            >
                              {t('general.en')}
                            </Typography>

                            {showPdf && invoicePdfLang === 'EN' && (
                              <InvPdf
                                account={account}
                                company={company}
                                values={values}
                                bankAccounts={bankAccounts}
                                logo={logo}
                                stamp={stamp}
                                update
                                invoicePdfLang={invoicePdfLang}
                                setShowPdf={setShowPdf}
                                showPdf={showPdf}
                                checked={checked}
                                freeAppMssg={freeAppMssg}
                              />
                            )}
                          </MenuItem>
                        </MenuStyled>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>

                <FormValues />
              </Form>
            );
          }}
        </Formik>
      </Container>

      <Popup
        title={
          itemForEdit
            ? itemForEdit.fieldName === 'bill_to'
              ? t('popup.title.client_details')
              : itemForEdit.fieldName === 'payment_term'
                ? t('popup.title.payment_term')
                : itemForEdit.fieldName === 'contract_terms'
                  ? t('popup.title.invoice_terms')
                  : itemForEdit.fieldName === 'email'
                    ? t('popup.title.email_basic')
                    : t('popup.title.invoice_item')
            : ''
        }
      >
        {itemForEdit?.fieldName === 'bill_to' && (
          <Contact addOrEdit={addOrEditAutoField} deleteAutoField={deleteAutoField} />
        )}

        {itemForEdit?.fieldName === 'payment_term' && (
          <TermOfPayment addOrEdit={addOrEditAutoField} deleteAutoField={deleteAutoField} />
        )}

        {itemForEdit?.fieldName === 'iteminv_invs' && (
          <ItemInv addOrEdit={addOrEditAutoField} deleteAutoField={deleteAutoField} />
        )}

        {itemForEdit?.fieldName === 'contract_terms' && (
          <ContractTerm addOrEdit={addOrEditAutoField} deleteAutoField={deleteAutoField} />
        )}

        {itemForEdit?.fieldName === 'email' && (
          <EmailBasic invoicePdfLang={invoicePdfLang} values={formValues} checked={checked} freeAppMssg={freeAppMssg} />
        )}
      </Popup>
    </>
  );
});

Inv.displayName = 'Inv';

export default Inv;
